import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef, getEntityRelations, useEntity } from '@backstage/plugin-catalog-react';
import { RELATION_PART_OF } from '@backstage/catalog-model';
import { RiFileCopyLine } from '@remixicon/react';
import {
  pluralize,
  RuleEntity,
  RuleSetEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  accountOf,
  affectedCount,
  maxSavingOf,
  rulesetsOf,
  severityOf,
  siblingTabPath,
} from './shared';
import styles from './entity-page.module.css';

/**
 * Rule page Overview, 1:1 with design/rule-page-mockup.html: stat strip
 * (failed checks / highest severity / saving / result), "what this rule
 * checks" + details, "included in" frameworks, cost impact, remediation —
 * or the unknown-locations variant when the scan could not evaluate the
 * rule. Severity on the strip is the WORST of the per-resource values
 * (spec.resources[].severity); the per-resource breakdown lives in the
 * Resources tab.
 */
export function RuleOverview() {
  const { entity } = useEntity();
  const rule = entity as RuleEntity;
  const catalogApi = useApi(catalogApiRef);
  const { pathname } = useLocation();
  const [copied, setCopied] = useState(false);

  const accountName = accountOf(rule);
  const rulesetRelations = getEntityRelations(rule, RELATION_PART_OF, {
    kind: 'ruleset',
  });

  // Framework memberships need the RuleSet entities (benchmark vs controls)
  // and the account rules (rules-per-framework counts).
  const [ruleSets, setRuleSets] = useState<RuleSetEntity[]>([]);
  const [accountRuleCounters, setAccountRuleCounters] = useState<Map<string, number>>(
    new Map(),
  );

  useEffect(() => {
    let cancelled = false;
    if (!accountName) {
      return () => {
        cancelled = true;
      };
    }
    catalogApi
      .getEntities({
        filter: { kind: 'RuleSet', 'spec.account': accountName },
      })
      .then(({ items }) => {
        if (!cancelled) {
          setRuleSets(items as RuleSetEntity[]);
        }
      })
      .catch(() => {
        // The page stays usable without framework types.
      });
    catalogApi
      .getEntities({ filter: { kind: 'Rule', 'spec.account': accountName } })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        const counters = new Map<string, number>();
        (items as RuleEntity[]).forEach(accountRule => {
          rulesetsOf(accountRule).forEach(rulesetName => {
            counters.set(rulesetName, (counters.get(rulesetName) ?? 0) + 1);
          });
        });
        setAccountRuleCounters(counters);
      })
      .catch(() => {
        // Counts are decorative; the links still render.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountName]);

  const resources = rule.spec.resources ?? [];
  const worst = severityOf(rule);
  const isUnknown = rule.spec.result === 'Unknown';
  const saving = maxSavingOf(rule);
  const remediation = rule.spec.remediation;
  const unknownLocations = useMemo(
    () => rule.spec.unknownLocations ?? [],
    [rule],
  );

  const frameworkRows = useMemo(
    () =>
      [...new Set([...rulesetRelations.map(rel => rel.name), ...rulesetsOf(rule)])].map(
        rulesetName => {
          const ruleSet = ruleSets.find(
            candidate => candidate.metadata.name === rulesetName,
          );
          const rawType = (
            ruleSet?.spec.rulesetType ??
            ruleSet?.spec.type ??
            'benchmark'
          ).toLowerCase();
          return {
            name: rulesetName,
            title: ruleSet?.metadata.title ?? rulesetName,
            isControls: rawType === 'controls',
            rulesTotal: accountRuleCounters.get(rulesetName) ?? 0,
          };
        },
      ),
    [rule, rulesetRelations, ruleSets, accountRuleCounters],
  );

  const cliText = useMemo(
    () => (remediation?.cli?.steps ?? []).join('\n'),
    [remediation],
  );

  const copyCli = async () => {
    try {
      await navigator.clipboard.writeText(cliText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access denied — the commands remain selectable.
    }
  };

  const detailFields = [
    { key: 'Rule code', value: rule.spec.type, mono: true },
    { key: 'Category', value: rule.spec.category },
    { key: 'Scope', value: rule.spec.scope },
    { key: 'Resource type', value: rule.spec.resourceType },
  ];

  return (
    <>
      <div className={styles.statStrip}>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Failed checks</span>
          <span className={styles.statV}>
            {affectedCount(rule)} <small>resources</small>
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Highest severity</span>
          <span className={styles.statV}>
            {worst} <small>worst of {resources.length || 0}</small>
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Est. monthly saving</span>
          <span className={`${styles.statV} ${saving ? styles.statMoney : ''}`}>
            {saving ? `$${saving.toLocaleString('en-US')}` : '—'}
          </span>
        </div>
        <div className={`${styles.card} ${styles.stat}`}>
          <span className={styles.statK}>Result</span>
          <span className={styles.statV}>{rule.spec.result ?? 'Unknown'}</span>
        </div>
      </div>

      <div className={styles.row2}>
        <div className={`${styles.card} ${styles.cardPad}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>What this rule checks</h3>
          </div>
          <p className={styles.muted} style={{ fontSize: 'var(--bui-font-size-3)' }}>
            {rule.metadata.description ?? 'No description provided for this rule.'}
          </p>
          <div className={styles.secTitle}>Rule details</div>
          <div className={styles.metaGrid}>
            {detailFields.map(field => (
              <div key={field.key}>
                <div className={styles.metaKey}>{field.key}</div>
                <div
                  className={`${styles.metaValue} ${field.mono ? styles.ruleCode : ''}`}
                  style={field.mono ? { fontSize: 12 } : undefined}
                >
                  {field.value ?? '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardPad}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Included in</h3>
            <span className={styles.cardHint}>RuleSet relations</span>
          </div>
          {frameworkRows.map(framework => (
            <Link
              key={framework.name}
              className={styles.fwRowLink}
              to={`/catalog/default/ruleset/${framework.name}`}
            >
              <span
                className={`${styles.fwTag} ${framework.isControls ? styles.fwTagControl : styles.fwTagBench}`}
              >
                {framework.title}
              </span>
              <span className={styles.fwRowLinkMeta}>
                {framework.isControls ? 'Controls' : 'Benchmark'} ·{' '}
                {pluralize(framework.rulesTotal, 'rule')}
              </span>
            </Link>
          ))}
          {frameworkRows.length === 0 && (
            <p className={styles.muted}>Not mapped to any framework.</p>
          )}
          {saving !== null && remediation?.impact && (
            <>
              <div className={styles.secTitle}>Cost impact</div>
              <div className={styles.impactBox}>{remediation.impact}</div>
            </>
          )}
          <div className={styles.secTitle}>Links</div>
          <p style={{ fontSize: 'var(--bui-font-size-3)' }}>
            <Link to={siblingTabPath(pathname, 'resources')}>
              Affected resources on this account →
            </Link>
            <br />
            <span className={styles.muted} style={{ fontSize: 'var(--bui-font-size-2)' }}>
              The Resources tab lists each affected resource with its own
              severity and note.
            </span>
          </p>
        </div>
      </div>

      {isUnknown && unknownLocations.length > 0 && (
        <>
          <div className={styles.secTitle}>Unknown locations</div>
          <div className={`${styles.card} ${styles.cardPad}`}>
            {unknownLocations.map(location => (
              <p key={location.name} style={{ fontSize: 'var(--bui-font-size-3)' }}>
                <b>{location.name}</b>
                {location.note ? ` — ${location.note}` : ''}
              </p>
            ))}
          </div>
        </>
      )}

      {!isUnknown && remediation && (
        <>
          <div className={styles.secTitle}>Remediation</div>
          <div className={`${styles.card} ${styles.cardPad}`}>
            <div className={styles.row2} style={{ marginTop: 0 }}>
              <div>
                <div className={styles.cardHead}>
                  <h3 className={styles.cardTitle}>Manual steps</h3>
                </div>
                {remediation.impact && (
                  <div className={styles.impactBox}>
                    Impact: {remediation.impact}
                  </div>
                )}
                {(remediation.manual?.steps ?? []).length > 0 && (
                  <ol className={styles.steps}>
                    {(remediation.manual?.steps ?? []).map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                )}
                {remediation.manual?.footer && (
                  <p className={styles.cliNote}>{remediation.manual.footer}</p>
                )}
              </div>
              <div>
                <div className={styles.cardHead}>
                  <h3 className={styles.cardTitle}>CLI</h3>
                </div>
                {cliText && (
                  <pre className={styles.cli}>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={copyCli}
                      title={copied ? 'Copied' : 'Copy'}
                      aria-label="Copy command"
                    >
                      <RiFileCopyLine size={15} />
                    </button>
                    {cliText}
                  </pre>
                )}
                {remediation.cli?.footer && (
                  <p className={styles.cliNote}>{remediation.cli.footer}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
